'use client';

import { useState } from 'react';
import { createPolicy, addPolicySection } from '@/af_actions/declarationActions';
import { useRouter } from 'next/navigation';
import { Plus, X } from 'lucide-react';

export default function HSCreateResolution({ userId, language = 'english', defaultCommitteeName, resolutionBasePath, defaultCategory, hideCategorySelection }: { userId: string | null; language?: 'english' | 'american'; defaultCommitteeName?: string; resolutionBasePath?: string; defaultCategory?: string; hideCategorySelection?: boolean }) {
    const [isOpen, setIsOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState(defaultCategory || 'General');
    // New states for the first paragraph
    const [initialContent, setInitialContent] = useState('');
    const [initialRationale, setInitialRationale] = useState('');

    const [loading, setLoading] = useState(false);
    const router = useRouter();


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!userId) {
            alert("You must be signed in to propose a new resolution. Sign in to continue.");
            return;
        }
        setLoading(true);
        try {

            const result = await createPolicy(title, description, category);

            // Pass content or translation based on selected language
            const content = language === 'american' ? null : initialContent;
            const translation = language === 'american' ? initialContent : null;

            await addPolicySection(result.id, content, initialRationale, 0, translation);

            router.push(`${resolutionBasePath ?? '/r'}/${result.slug}`);
        } catch (error) {
            console.error(error);
            alert('Failed to create declaration and initial paragraph');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => {
                    if (!userId) {
                        alert("You must be signed in to propose a new resolution. Sign in to continue.");
                        return;
                    }
                    setIsOpen(true)
                }}
                className="w-full mb-8 py-4 border-2 border-dashed border-amber-300 rounded-lg text-amber-800 hover:bg-amber-100/50 hover:border-amber-400 transition-all flex items-center justify-center gap-2 font-serif font-bold uppercase tracking-widest text-sm"
            >
                <Plus size={20} />
                Propose New Resolution
            </button>
        );
    }

    return (
        <div className="bg-white text-black border-2 border-amber-500 p-6 rounded-lg mb-8 shadow-lg animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h2 className="text-xl font-bold text-amber-900 font-serif">New Resolution</h2>
                    {defaultCommitteeName && (
                        <p className="text-xs uppercase tracking-widest text-amber-700 mt-1 font-sans">
                            Committee: {defaultCommitteeName}
                        </p>
                    )}
                </div>
                <button
                    onClick={() => setIsOpen(false)}
                    className="text-amber-500 hover:text-amber-700 transition-colors"
                >
                    <X size={24} />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-amber-800 mb-1 font-sans">Resolution Title</label>
                    <input
                        required
                        placeholder="Title"
                        className="w-full p-3 border border-amber-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50/30 font-serif text-lg"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </div>

               <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-amber-800 mb-1 font-sans">Category</label>
                    {hideCategorySelection ? (
                        <div className="w-full p-3 border border-amber-200 rounded bg-amber-50/30 font-serif text-sm text-slate-700">
                            {category === 'General' ? "Human" : category === 'Moral' ? 'Religious' : 'Divine'}
                        </div>
                    ) : (
                        <select
                            required
                            className="w-full p-3 border border-amber-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50/30 font-serif"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        >
                            <option value="General">Moral</option>
                            <option value="Moral">Religious</option>
                            <option value="Divine">Divine</option>
                        </select>
                    )}
                </div>

                <div className="border-t border-amber-100 pt-4">
                    <label className="block text-xs font-bold uppercase tracking-widest text-amber-800 mb-1 font-sans">Preamble (Content)</label>
                    <textarea
                        //required
                        placeholder="Description"
                        rows={4}
                        className="w-full p-3 border border-amber-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50/30 font-serif"
                        value={initialContent}
                        onChange={(e) => setInitialContent(e.target.value)}
                    />
                </div>
                {/*}                <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-amber-800 mb-1 font-sans">Rationale / Preamble</label>
                    <input
                        placeholder="Why"
                        className="w-full p-3 border border-amber-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50/30 font-serif text-sm italic"
                        value={initialRationale}
                        onChange={(e) => setInitialRationale(e.target.value)}
                    />
                </div>
*/}
                <div className="flex gap-3 pt-4">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-amber-800 text-white px-6 py-3 rounded font-bold uppercase tracking-widest text-sm hover:bg-amber-900 disabled:opacity-50 transition-colors shadow-md"
                    >
                        {loading ? 'Publishing...' : 'Publish'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="px-6 py-3 border border-amber-200 text-amber-800 rounded font-bold uppercase tracking-widest text-sm hover:bg-amber-50 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}